import { useCallback, useEffect, useState } from 'react';
import { getCustomerList, getCustomerLoading, getCustomerPage, getCustomers, getHasMoreCustomers } from './CustomerSlice';
import { toTitleCase } from '../../../Utility/toTitleCase';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { Customer } from '../../../Types/Customer';
import { Button, Card, Col, Container, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from 'reactstrap';
import { IconButton, InputAdornment, OutlinedInput } from '@mui/material';
import { Clear } from '@mui/icons-material';
import { useInfiniteScroll } from '../../../Hooks/useInfiniteScroll';
import { getWriterId } from '../../SalesDep/Managers/SalesRepView/WriterSlice';
import Loading from '../../../Components/Loading';

type props = {
    visible: boolean;
    onClose: (id: string | null) => void;
    onClosed?: () => void;
};

type Data = {
    id: string;
    name: string;
    contact: string;
};

const CustomerSearchModal = ({ visible, onClose, onClosed }: props) => {
    const dispatch = useAppDispatch();
    const customers = useAppSelector(getCustomerList) as Customer[];
    const page = useAppSelector(getCustomerPage);
    const hasMore = useAppSelector(getHasMoreCustomers);
    const token = useAppSelector((state) => state.auth.token);
    const currentWriter = useAppSelector(getWriterId);
    const customerLoading = useAppSelector(getCustomerLoading);

    const [query, setQuery] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [searchData, setSearchData] = useState<Customer[]>([]);

    useEffect(() => {
        if (currentWriter) {
            dispatch(getCustomers({ page: page + 1, writerId: currentWriter }));
        } else {
            dispatch(getCustomers({ page: page + 1 }));
        }
    }, [dispatch, currentWriter]);

    const loadMoreData = useCallback(() => {
        if (customerLoading || !hasMore) return;
        if (currentWriter) {
            dispatch(getCustomers({ page: page + 1, writerId: currentWriter }));
        } else {
            dispatch(getCustomers({ page: page + 1 }));
        }
    }, [dispatch, loading, hasMore, page, currentWriter]);

    const handleSearch = (text: string) => {
        setQuery(text);
    };

    // useEffect(() => {
    //     if (!query) {
    //         setSearchData([]);
    //         return;
    //     }

    //     setSearchData(
    //         customers.filter(
    //             (cust) =>
    //                 cust.id?.toString().includes(query) ||
    //                 cust.name?.toLowerCase().includes(query.toLowerCase()) ||
    //                 cust.contact?.toLowerCase().includes(query.toLowerCase())
    //         )
    //     );
    // }, [query, customers]);

    const searchCustomer = async (query: string) => {
        try {
            setError(null);
            setLoading(true);
            const baseUrl = 'https://customer.hirsch.com/account/v2/employee/customer';
    
            const res = await fetch(`${baseUrl}?lookup=${query}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': btoa(`Bearer ${token}`)
                },
            });
            const json = await res.json();
            
            if (!res.ok) throw new Error('Failed to Fetch');
            if (!json.ack) throw new Error(json.errorMesssage);
            if (!json.customers[0]) setError('No Results Found')
            setSearchData(json.customers);
        } catch (e: any) {
            setError(e)
        } finally {
            setLoading(false);
        }
    };

    const renderFooter = () => {
        if (!customerLoading || !hasMore) return null;

        return (
            <div style={{ height: 80,}} className='justify-content-center d-flex pt-3'>
                <Spinner 
                    color="primary"
                    style={{ width: '3rem', height: '3rem'}}
                />
            </div>
        );
    };

    const renderItem = ({ item }: { item: Data }) => {
        return (
            <Card
                className='shadow'
                style={{ marginBottom: 2, cursor: 'pointer' }}
                onClick={() => onClose(item.id)}
            >
                <div style={{ display: 'flex', gap: 10, padding: 10 }}>
                    <div>
                        <span className='d-block'>Cust ID: </span>
                        <span className='d-block'>Cust Name: </span>
                    </div>
                    <div>
                        <span className='d-block'>{item.id}</span>
                        <span className='d-block'>{toTitleCase(item.name)}</span>
                    </div>
                </div>
            </Card>
        )
    };

    const sentinelRef = useInfiniteScroll({
        onEndReached: loadMoreData,
        enabled: hasMore && !loading,
    });

    return (
        <div>
            <Modal
                isOpen={visible}
                onClosed={() => onClose(null)}
                fullscreen
                onDismiss={onClosed}
                unmountOnClose
            >
                <ModalHeader 
                    className='bg-primary d-flex justify-content-center'
                    style={{ color: 'white', }}
                >
                    <Button 
                        title='Close'
                        onClick={() => onClose(null)}
                        className="bg-transparent border-0 fw-bold text-white position-absolute start-0 ms-2"
                    >
                        Close
                    </Button>
                    <span 
                        style={{ color: 'white', fontWeight: 'bold', fontSize: 18, }}
                    >Customer Search</span>
                </ModalHeader>
                <ModalHeader className='justify-content-center border-0 d-block'>
                    <Container className='bg-white'>
                        <OutlinedInput 
                            id='search'
                            style={{ width: '100%' }}
                            value={query}
                            className='bg-white shadow-sm'
                            onChange={(e) => handleSearch(e.target.value)}
                            placeholder='Search'
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    searchCustomer(query);
                                }
                            }}
                            endAdornment={
                                <InputAdornment position='end'>
                                    <IconButton
                                        onClick={() => {
                                            setQuery('');
                                            handleSearch('');
                                            setSearchData([]);
                                            setError(null);
                                            if (currentWriter) {
                                                dispatch(getCustomers({ writerId: currentWriter }));
                                            } else {
                                                dispatch(getCustomers({}));
                                            }
                                        }}
                                        edge='end'
                                    >
                                        {query ? <Clear /> : null}
                                    </IconButton>
                                </InputAdornment>
                            }
                        />
                        {error &&
                            <div style={{ paddingBlockStart: 10 }}>
                                <span style={{ color: 'red' }}>{error}</span>
                            </div>
                        }
                    </Container>
                </ModalHeader>
                <ModalBody>
                    <Container className='bg-white'>
                        <div className='mt-3'>
                            {loading
                            ? (
                                <Loading />
                            ) : (
                                <div>
                                    <div>
                                        {
                                            (query && searchData.length ? searchData : customers).map((item) => (
                                                renderItem({item})
                                            ))
                                        }
                                    </div>
                                    {!loading &&
                                        <div 
                                            ref={sentinelRef}
                                            style={{
                                                minHeight: 20,
                                                width: '100%',
                                            }}
                                        />
                                    }
                                    {renderFooter()}
                                </div>
                            )
                        }
                        </div>
                    </Container>
                </ModalBody>
            </Modal>
        </div>
    )

};

export default CustomerSearchModal;